import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Resource list carousel progress component
 * Displays a progress indicator and navigation arrows for carousel slideshows
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} progressFill
 *
 * @extends {Component<Refs>}
 */
class ResourceListCarouselProgress extends Component {
  requiredRefs = ['progressFill'];

  /** @type {HTMLElement[] | null} */
  #carouselSlides = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {Element | null} */
  #slideshow = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #scrollRafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#slideshow = this.closest('slideshow-component');
    if (!this.#slideshow) return;

    this.#scroller = this.#slideshow.querySelector('slideshow-slides');
    if (!this.#scroller) return;

    this.#carouselSlides = this.#getCarouselSlides();
    const actualSlideCount = this.#carouselSlides.length;
    if (actualSlideCount === 0) return;

    const initialCarouselIndex = this.#getInitialCarouselIndex(this.#slideshow);
    this.#updateProgress(initialCarouselIndex, actualSlideCount);
    this.#syncArrowState();

    this.#resizeObserver = new ResizeObserver(() => {
      this.#syncArrowState();
    });
    this.#resizeObserver.observe(this.#scroller);

    this.#scroller.addEventListener('scroll', this.#handleScroll);
    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
  }

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateFromScrollPosition();
    });
  };

  #updateFromScrollPosition() {
    if (!this.#scroller) return;

    const carouselSlides = this.#getCarouselSlides();
    const totalSlides = carouselSlides.length;
    if (totalSlides === 0) return;

    const scrollLeft = this.#scroller.scrollLeft;
    const maxScroll = this.#scroller.scrollWidth - this.#scroller.clientWidth;

    if (maxScroll > 0) {
      const scrollProgress = scrollLeft / maxScroll;
      const progress = (1 + (totalSlides - 1) * scrollProgress) / totalSlides;
      this.#updateProgressFromScroll(progress);
    } else {
      this.#updateProgressFromScroll(1 / totalSlides);
    }

    this.#syncArrowState();
  }

  /**
   * Get carousel slides array (cached)
   * @returns {HTMLElement[]}
   */
  #getCarouselSlides() {
    if (this.#carouselSlides) return this.#carouselSlides;

    if (!this.#scroller) {
      const slideshow = this.closest('slideshow-component');
      if (!slideshow) return [];
      this.#scroller = slideshow.querySelector('slideshow-slides');
      if (!this.#scroller) return [];
    }

    this.#carouselSlides = Array.from(
      this.#scroller.querySelectorAll('slideshow-slide.resource-list__slide:not([hidden])')
    ).map((el) => /** @type {HTMLElement} */ (el));

    return this.#carouselSlides;
  }

  /**
   * Get initial carousel slide index from slideshow
   * @param {Element | null} slideshow
   * @returns {number}
   */
  #getInitialCarouselIndex(slideshow) {
    if (!this.#scroller || !slideshow) return 0;

    const initialSlideAttr = slideshow.getAttribute('initial-slide');
    const initialSlideshowIndex = initialSlideAttr ? parseInt(initialSlideAttr, 10) : 0;
    const allSlides = Array.from(this.#scroller.querySelectorAll('slideshow-slide:not([hidden])'));
    const initialSlide = allSlides[initialSlideshowIndex];
    
    if (!initialSlide) return 0;

    return this.#getCarouselSlideIndex(initialSlide);
  }

  /**
   * Get carousel slide index from a slide element
   * Finds the index of the slide within carousel slides only (excluding nested product media)
   * If the slide is a nested product media slide, finds its parent carousel slide
   * @param {Element | null} slideElement - The slide element from the event
   * @returns {number} - Index within carousel slides only, or 0 if not found
   */
  #getCarouselSlideIndex(slideElement) {
    if (!slideElement) return 0;

    const carouselSlides = this.#getCarouselSlides();
    if (carouselSlides.length === 0) return 0;

    let targetSlide = slideElement;

    if (!slideElement.classList.contains('resource-list__slide')) {
      const parentCarouselSlide = slideElement.closest('slideshow-slide.resource-list__slide');
      if (!parentCarouselSlide) return 0;
      targetSlide = parentCarouselSlide;
    }

    const carouselIndex = carouselSlides.indexOf(/** @type {HTMLElement} */ (targetSlide));
    return carouselIndex >= 0 ? carouselIndex : 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scroller) {
      this.#scroller.removeEventListener('scroll', this.#handleScroll);
      this.#scroller = null;
    }

    if (this.#slideshow) {
      this.#slideshow.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
      this.#slideshow = null;
    }

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  #handleSlideshowSelect = () => {
    this.#syncArrowState();
  };

  #syncArrowState() {
    const previousButton = this.querySelector('.slideshow-control--previous');
    const nextButton = this.querySelector('.slideshow-control--next');
    if (!(previousButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) return;

    if (!this.#scroller) {
      previousButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    const canScroll = this.#scroller.scrollWidth > this.#scroller.clientWidth + 1;
    if (!canScroll) {
      previousButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    const carouselSlides = this.#getCarouselSlides();
    if (carouselSlides.length <= 1) {
      previousButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    const scrollLeft = this.#scroller.scrollLeft;
    const maxScroll = this.#scroller.scrollWidth - this.#scroller.clientWidth;
    const threshold = 2;

    previousButton.disabled = scrollLeft <= threshold;
    nextButton.disabled = scrollLeft >= maxScroll - threshold;
  }

  /**
   * Update the progress fill width based on current slide index
   * @param {number} currentIndex - Current slide index (0-based)
   * @param {number} totalSlides - Total number of slides
   */
  #updateProgress(currentIndex, totalSlides) {
    const progress = totalSlides > 0 ? ((currentIndex + 1) / totalSlides) : 0;
    this.#updateProgressFromScroll(progress);
  }

  /**
   * Apply progress value to the progress fill element
   * @param {number} progress - Progress value (0-1)
   */
  #updateProgressFromScroll(progress) {
    const { progressFill } = this.refs;
    if (!progressFill) return;

    progressFill.style.setProperty('--progress-width', progress.toString());
    progressFill.setAttribute('aria-valuenow', Math.round(progress * 100).toString());
    progressFill.setAttribute('aria-valuemax', '100');
  }

  /**
   * Delegate previous navigation to the slideshow component
   * @param {Event} event
   */
  previous(event) {
    const slideshow = this.closest('slideshow-component');
    if (slideshow && 'previous' in slideshow) {
      const prevMethod = slideshow.previous;
      if (typeof prevMethod === 'function') {
        prevMethod.call(slideshow, event);
      }
    }
  }

  /**
   * Delegate next navigation to the slideshow component
   * @param {Event} event
   */
  next(event) {
    const slideshow = this.closest('slideshow-component');
    if (slideshow && 'next' in slideshow) {
      const nextMethod = slideshow.next;
      if (typeof nextMethod === 'function') {
        nextMethod.call(slideshow, event);
      }
    }
  }
}

if (!customElements.get('resource-list-carousel-progress')) {
  customElements.define('resource-list-carousel-progress', ResourceListCarouselProgress);
}
